import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';
import { NbToastrService, NbDialogService } from '@nebular/theme';
import { DataSource } from 'ng2-smart-table/lib/lib/data-source/data-source';

import { CustomFoodData } from '../../../@core/interfaces/common/CalorieFriend/custom-food';
import { ConfirmMessageDialogComponent } from 'app/@components/confirm-message-dialog/confirm-message-dialog.component';
import { CustomFoodComponent } from '../custom-food/custom-food.component';

/**
 * Component for listing the Public Food items
 */
@Component({
  selector: 'ngx-public-foods-list',
  templateUrl: './public-foods-list.component.html',
  styleUrls: ['./public-foods-list.component.scss'],
})
export class PublicFoodsListComponent implements OnInit {
  MyFoodListLabel = 'Food Groups';
  AddLabel = 'Add New';

  NameLabel = 'Name';
  DescriptionLabel = 'Description';
  CaloriesLabel = 'Calories';
  FatsLabel = 'Fats';
  CarbsLabel = 'Carbs';
  ProteinsLabel = 'Proteins';
  UpdatedDateLabel = 'Updated Date';

  SuccessDeleteMyFood = 'Successfully deleted Custom Food';
  FailDeleteMyFood = 'Problem occurred delelting Custom Food';
  BackLabel = 'Back';

  CustomFoodSource: DataSource;
  protected readonly unsubscribe$ = new Subject<void>();

  /** MyFoodList ctor */
  constructor(
    private customFoodService: CustomFoodData,
    private router: Router,
    private route: ActivatedRoute,
    private toasterService: NbToastrService,
    private dialogService: NbDialogService,
  ) {
    this.CustomFoodSource = this.customFoodService.gridDataSource;
    this.CustomFoodSource.setFilter(
      [
        {
          field: 'OnlyPublic',
          search: true,
        },
      ],
      true,
      false,
    );
  }

  /**
   * Settings of the ng2 smart table of Custom Foods
   */
  MyFoodTableSettings = {
    hideSubHeader: true,
    mode: 'external',
    actions: {
      columnTitle: $localize`:@@Actions:Actions`,
      position: 'right',
      add: false,
    },

    edit: {
      editButtonContent:
        '<img src="assets/icons/added_oultine_modify_pencil.svg" title="' +
        $localize`:@@Edit:Edit` +
        '" class="img-archive" width="24" height="24">',
    },
    delete: {
      deleteButtonContent:
        '<img src="assets/icons/trash-2-outline.svg" title="' +
        $localize`:@@Delete:Delete` +
        '" class="img-archive" width="24" height="24">',
    },

    columns: {
      name: { title: $localize`:@@Name:Name`, type: 'string', filter: false },
      description: {
        title: $localize`:@@Description:Description`,
        type: 'string',
        filter: false,
      },
      totalCalories: {
        title: $localize`:@@Calories:Calories`,
        type: 'number',
        filter: false,
      },
      fats: {
        title: $localize`:@@Fats:Fats`,
        type: 'number',
        filter: false,
      },
      carbohydrate: {
        title: $localize`:@@Carbs:Carbs`,
        type: 'number',
        filter: false,
      },
      protein: {
        title: $localize`:@@Protein:Protein`,
        type: 'number',
        filter: false,
      },
      updatedDate: {
        title: $localize`:@@LastUpdate:Last Update`,
        type: 'date',
        filter: false,
      },
    },
  };

  /**
   * Initializes the component.
   * It sets the default sorting for the table.
   */
  ngOnInit(): void {
    this.CustomFoodSource.setSort([
      { field: 'updatedDate', direction: 'desc' },
    ]);
  }

  /**
   * It opens the component 'CustomFoodDefComponent' to create a new Custom Food item as the pop up dialog
   */
  AddCustomFood() {
    this.dialogService
      .open(CustomFoodComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          id: null,
          isPublic: true,
        },
      })
      .onClose.subscribe((goal) => {
        this.CustomFoodSource.refresh();
      });
  }

  /**
   * It opens the component 'CustomFoodDefComponent' to edit an existing Custom Food item as the pop up dialog
   * @param $event Event object
   */
  onEdit($event) {
    this.dialogService
      .open(CustomFoodComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          id: $event.data.id,
          isPublic: true,
        },
      })
      .onClose.subscribe((goal) => {
        this.CustomFoodSource.refresh();
      });
  }

  /**
   * Event handler of the 'Click' event of the 'Delete' action.
   * It deletes a Custom Food with {id} (given by $event object).
   * @param $event Event object
   */
  onDelete($event) {
    this.dialogService
      .open(ConfirmMessageDialogComponent, {
        closeOnEsc: true,
        closeOnBackdropClick: true,
        context: { Message: 'Are you sure to delete this public food?' },
      })
      .onClose.subscribe((result) => {
        if (result) {
          const observable = this.customFoodService.delete($event.data.id);

          observable.pipe(takeUntil(this.unsubscribe$)).subscribe(
            () => {
              this.handleMyFoodDeleteSuccessResponse();
            },
            (err) => {
              this.handleMyFoodDeleteWrongResponse();
            },
          );
        }
      });
  }

  searchTimeout = null;

  /**
   * Event handler of the auto-complete action.
   * Searches the items within Name or Description fields by the {query} value
   * @param query Query string to search items
   */
  onSearch(query: string = '') {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.searchTimeout = setTimeout(
      function (self) {
        self.CustomFoodSource.setFilter(
          [
            {
              field: 'OnlyPublic',
              search: true,
            },
            {
              field: 'NameOrDescription',
              search: query,
            },
          ],
          true,
        );
      },
      700,
      this,
    );
  }

  /**
   * Handler of Success event of Delete action of an existing item
   */
  handleMyFoodDeleteSuccessResponse() {
    this.toasterService.success('', this.SuccessDeleteMyFood);
    // refresh screen
    this.CustomFoodSource.refresh();
  }

  /**
   * Handler of Fail event of Delete action of an existing item
   */
  handleMyFoodDeleteWrongResponse() {
    this.toasterService.danger('', this.FailDeleteMyFood);
  }

  Back() {}
}
